import React, { useState } from 'react';
import { Clock, Plus, Trash2, Euro, Calendar } from 'lucide-react';
import './TimeSlotManager.css';

export default function TimeSlotManager({ slots = [], onChange, disabled = false }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSlot, setNewSlot] = useState({
    name: '',
    startTime: '',
    endTime: '',
    price: '',
    maxCapacity: ''
  });

  const handleAddSlot = () => {
    if (!newSlot.name || !newSlot.startTime || !newSlot.endTime || !newSlot.price || !newSlot.maxCapacity) {
      alert('Por favor, preenche todos os campos do slot');
      return;
    }

    if (newSlot.startTime >= newSlot.endTime) {
      alert('A hora de fim deve ser posterior à hora de início');
      return;
    }

    if (parseFloat(newSlot.price) <= 0) {
      alert('O preço deve ser maior que zero');
      return;
    }

    if (parseInt(newSlot.maxCapacity) <= 0) {
      alert('A capacidade deve ser maior que zero');
      return;
    }

    const slot = {
      id: Date.now(), 
      name: newSlot.name.trim(),
      startTime: newSlot.startTime,
      endTime: newSlot.endTime,
      price: parseFloat(newSlot.price),
      maxCapacity: parseInt(newSlot.maxCapacity),
      currentBookings: 0
    };

    onChange([...slots, slot]);
    
    setNewSlot({
      name: '',
      startTime: '',
      endTime: '',
      price: '',
      maxCapacity: ''
    });
    setShowAddForm(false);
  };

  const handleRemoveSlot = (index) => {
    if (window.confirm('Tens a certeza que queres remover este slot?')) {
      const newSlots = slots.filter((_, i) => i !== index);
      onChange(newSlots);
    }
  };

  const handleUpdateSlot = (index, field, value) => {
    const newSlots = [...slots];
    newSlots[index] = {
      ...newSlots[index],
      [field]: field === 'price' ? parseFloat(value) : field === 'maxCapacity' ? parseInt(value) : value
    };
    onChange(newSlots);
  };

  const formatTime = (time) => {
    return time;
  };

  const calculateDuration = (start, end) => {
    const startMinutes = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
    const endMinutes = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1]);
    const duration = endMinutes - startMinutes;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}min`;
    }
  };

  return (
    <div className="timeslot-manager">
      <div className="timeslot-header">
        <div>
          <h3 className="timeslot-title">
            <Clock className="icon" />
            Slots de Horário
          </h3>
          <p className="timeslot-subtitle">
            Define os horários disponíveis e os preços para cada período
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddForm(!showAddForm)}
          className="timeslot-add-button"
          disabled={disabled}
        >
          <Plus className="icon-small" />
          {showAddForm ? 'Cancelar' : 'Adicionar Slot'}
        </button>
      </div>

      {showAddForm && (
        <div className="timeslot-add-form">
          <h4 className="form-section-title">Novo Slot de Horário</h4>
          
          <div className="form-group">
            <label className="form-label">Nome do Slot *</label>
            <input
              type="text"
              value={newSlot.name}
              onChange={(e) => setNewSlot({ ...newSlot, name: e.target.value })}
              className="form-input"
              placeholder="Ex: Sessão da Manhã, Turma A, Horário Premium..."
              disabled={disabled}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Hora de Início *</label>
              <input
                type="time"
                value={newSlot.startTime}
                onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                className="form-input"
                disabled={disabled}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Hora de Fim *</label>
              <input
                type="time"
                value={newSlot.endTime}
                onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                className="form-input"
                disabled={disabled}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Preço (€) *</label>
              <div className="input-with-icon">
                <Euro className="input-icon" />
                <input
                  type="number"
                  value={newSlot.price}
                  onChange={(e) => setNewSlot({ ...newSlot, price: e.target.value })}
                  min="0"
                  step="0.01"
                  className="form-input with-icon"
                  placeholder="0.00"
                  disabled={disabled}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Capacidade Máxima *</label>
              <input
                type="number"
                value={newSlot.maxCapacity}
                onChange={(e) => setNewSlot({ ...newSlot, maxCapacity: e.target.value })}
                min="1"
                className="form-input"
                placeholder="Ex: 20"
                disabled={disabled}
              />
            </div>
          </div>

          <div className="form-actions-inline">
            <button
              type="button"
              onClick={handleAddSlot}
              className="btn-primary-small"
              disabled={disabled}
            >
              <Plus className="icon-small" />
              Adicionar Slot
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewSlot({ name: '', startTime: '', endTime: '', price: '', maxCapacity: '' });
              }}
              className="btn-secondary-small"
              disabled={disabled}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {slots.length === 0 ? (
        <div className="timeslot-empty">
          <Calendar className="empty-icon" />
          <p className="empty-text">Nenhum slot de horário definido</p>
          <p className="empty-subtext">
            Adiciona pelo menos um slot para que os utilizadores possam fazer reservas
          </p>
        </div>
      ) : (
        <div className="timeslot-list">
          {slots.map((slot, index) => (
            <div key={slot.id || index} className="timeslot-card">
              <div className="timeslot-card-header">
                <div className="timeslot-header-content">
                  <div className="timeslot-name-section">
                    <input
                      type="text"
                      value={slot.name || ''}
                      onChange={(e) => handleUpdateSlot(index, 'name', e.target.value)}
                      className="slot-name-input"
                      placeholder="Nome do slot"
                      disabled={disabled}
                    />
                  </div>
                  <div className="timeslot-time">
                    <Clock className="icon-small" />
                    <span className="time-range">
                      {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                    </span>
                    <span className="duration-badge">
                      {calculateDuration(slot.startTime, slot.endTime)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveSlot(index)}
                  className="timeslot-remove-button"
                  disabled={disabled}
                  title="Remover slot"
                >
                  <Trash2 className="icon-small" />
                </button>
              </div>

              <div className="timeslot-card-body">
                <div className="timeslot-info-grid">
                  <div className="timeslot-info-item">
                    <label className="info-label">Preço</label>
                    <div className="info-value">
                      <Euro className="icon-tiny" />
                      <input
                        type="number"
                        value={slot.price}
                        onChange={(e) => handleUpdateSlot(index, 'price', e.target.value)}
                        min="0"
                        step="0.01"
                        className="inline-input"
                        disabled={disabled}
                      />
                    </div>
                  </div>

                  <div className="timeslot-info-item">
                    <label className="info-label">Capacidade</label>
                    <div className="info-value">
                      <input
                        type="number"
                        value={slot.maxCapacity}
                        onChange={(e) => handleUpdateSlot(index, 'maxCapacity', e.target.value)}
                        min="1"
                        className="inline-input"
                        disabled={disabled}
                      />
                      <span className="capacity-text">lugares</span>
                    </div>
                  </div>

                  {slot.currentBookings !== undefined && (
                    <div className="timeslot-info-item">
                      <label className="info-label">Reservas</label>
                      <div className="info-value">
                        <span className="bookings-count">
                          {slot.currentBookings} / {slot.maxCapacity}
                        </span>
                        {slot.currentBookings >= slot.maxCapacity && (
                          <span className="badge-full">Esgotado</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}